import { parseJsonValueWithRepair } from './jsonValueUtils';

export const looksLikeHtmlPayload = (value) => {
  const text = String(value ?? '').trim();
  if (!text) {
    return false;
  }
  return /<\s*(html|body|div|span|table|thead|tbody|tr|td|th|ul|ol|li|p|h[1-6]|svg|canvas|script|style|iframe)\b/i.test(text);
};

const isStructuredWidgetValue = (value) => (
  value !== null
  && typeof value === 'object'
);

/**
 * Returns the first structured value carried by a widget response.
 * Visual widgets keep their source rows in data_json/data while text widgets
 * usually put the JSON directly in answer.
 */
export const extractWidgetStructuredData = (response, { includeDataFields = true } = {}) => {
  if (response === null || response === undefined) {
    return null;
  }

  const candidates = [];
  if (response && typeof response === 'object' && !Array.isArray(response)) {
    const hasAnswerField = Object.prototype.hasOwnProperty.call(response, 'answer');
    if (hasAnswerField) {
      candidates.push(response.answer);
    }
    const answerIsEmpty = !hasAnswerField
      || response.answer === null
      || response.answer === undefined
      || (typeof response.answer === 'string' && !response.answer.trim());
    if (includeDataFields || answerIsEmpty) {
      if (Object.prototype.hasOwnProperty.call(response, 'data_json')) {
        candidates.push(response.data_json);
      }
      if (Object.prototype.hasOwnProperty.call(response, 'data')) {
        candidates.push(response.data);
      }
    }
    if (candidates.length === 0) {
      candidates.push(response);
    }
  } else {
    candidates.push(response);
  }

  for (const candidate of candidates) {
    const parsed = parseJsonValueWithRepair(candidate);
    if (isStructuredWidgetValue(parsed)) {
      return parsed;
    }
  }

  return null;
};

export const inferWidgetRenderType = (parsedResponse, fallbackType = 'text') => {
  const explicitType = String(parsedResponse?.type || '').trim().toLowerCase();
  if (explicitType === 'html-with-js') {
    return 'html-with-js';
  }
  if (explicitType) {
    return explicitType;
  }

  if (parsedResponse && typeof parsedResponse === 'object' && !Array.isArray(parsedResponse)) {
    const answerCandidate = parsedResponse.answer ?? parsedResponse.value ?? '';
    if (looksLikeHtmlPayload(answerCandidate)) {
      return 'html-with-js';
    }
  } else if (looksLikeHtmlPayload(parsedResponse)) {
    return 'html-with-js';
  }

  return String(fallbackType || 'text').trim().toLowerCase() || 'text';
};

export const normalizeWidgetResponsePayload = (response) => {
  if (response && typeof response === 'object' && !Array.isArray(response)) {
    return { ...response };
  }

  if (typeof response === 'string') {
    const trimmed = response.trim();
    if (!trimmed) {
      return {};
    }

    try {
      const parsed = JSON.parse(trimmed);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return parsed;
      }
      return { answer: parsed };
    } catch (error) {
      return { answer: response };
    }
  }

  if (response === null || response === undefined) {
    return {};
  }

  return { answer: response };
};

/**
 * Materialize the compact visual-widget contract used by the backend.
 *
 * Stored visual responses keep the reusable template separate from its rows
 * (`ui_template` + `data_json`).  Live websocket updates can arrive before a
 * server-side materializer has run, so normalize that contract at the client
 * boundary as well.  This deliberately knows only the technical rendering
 * contract; the row shape and field names remain entirely data-driven.
 */
export const materializeWidgetResponsePayload = (response) => {
  const normalized = normalizeWidgetResponsePayload(response);
  if (!normalized || typeof normalized !== 'object' || Array.isArray(normalized)) {
    return normalized;
  }

  const hasAnswer = Object.prototype.hasOwnProperty.call(normalized, 'answer');
  const answerValue = normalized.answer;
  const answerIsPresent = hasAnswer
    && answerValue !== null
    && answerValue !== undefined
    && (typeof answerValue !== 'string' || answerValue.trim());
  if (answerIsPresent) {
    return normalized;
  }

  const template = typeof normalized.ui_template === 'string'
    ? normalized.ui_template
    : (typeof normalized.uiTemplate === 'string' ? normalized.uiTemplate : '');
  if (!template.trim()) {
    return normalized;
  }

  let dataPayload = '[]';
  if (normalized.data_json !== undefined && normalized.data_json !== null) {
    if (typeof normalized.data_json === 'string') {
      dataPayload = normalized.data_json.trim() || '[]';
    } else {
      try {
        dataPayload = JSON.stringify(normalized.data_json);
      } catch (error) {
        dataPayload = '[]';
      }
    }
  } else if (normalized.data !== undefined && normalized.data !== null) {
    try {
      dataPayload = typeof normalized.data === 'string'
        ? (normalized.data.trim() || '[]')
        : JSON.stringify(normalized.data);
    } catch (error) {
      dataPayload = '[]';
    }
  }

  const materialized = template.replace(
    /const\s+MY_DATA\s*=\s*\[\s*\]\s*;?/i,
    `const MY_DATA = ${dataPayload};`
  );

  if (materialized === template && !hasAnswer) {
    return {
      ...normalized,
      answer: template,
      type: 'html-with-js'
    };
  }

  return {
    ...normalized,
    answer: materialized,
    type: 'html-with-js'
  };
};
